import { ReactComponent as MoveBackIcon } from 'assets/icons/move-back.svg';
import AddTask from 'containers/Plan/AddTask';
import {
    ProjectId,
    ProjectRef,
    TaskState,
    TaskStateObject,
    WorkspaceId,
} from 'core/model';
import Button from 'layout/atoms/Button';
import Container from 'layout/atoms/Container';
import Dropdown from 'layout/atoms/Dropdown';
import Grid from 'layout/atoms/Grid';
import TaskExecute from 'layout/atoms/TaskExecute';
import PlannedTasksList from 'layout/organisms/PlannedTasksList/PlannedTasksList';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store';
import {
    addTask,
    hideCompletedTasksAction,
    idleAllPlannedTasksAction,
} from 'store/tasksThunks';
import { selectWorkspaceId } from 'store/userSlice';
import { selectWorkspaceData, WorkspaceState } from 'store/workspaceSlice';

export type PlannedTaskOrigin = 'workspace' | 'project' | 'automation';

export type PlannedTask = {
    task: TaskStateObject;
    color?: string | null;
    projectName?: string;
    projectRef: ProjectRef;
    origin: PlannedTaskOrigin;
};

const ExecutePage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const workspaceId = useSelector(selectWorkspaceId) || '';
    const workspaceData: WorkspaceState = useSelector(selectWorkspaceData);

    const activeTasks: PlannedTask[] = [
        ...workspaceData.tasks
            .filter(
                (t) => t.done || workspaceData.plannedTasks.indexOf(t.id) >= 0
            )
            .map((task) => {
                return {
                    task,
                    projectRef: {
                        workspaceId: workspaceData.id as WorkspaceId,
                        projectId: workspaceData.id as ProjectId,
                    },
                    origin: 'workspace' as PlannedTaskOrigin,
                };
            }),
        ...workspaceData.projects.flatMap((p) => {
            return p.tasks
                .filter(
                    (t: any) =>
                        t.done || workspaceData.plannedTasks.indexOf(t.id) >= 0
                )
                .map((task: any) => {
                    return {
                        task,
                        color: p.color,
                        projectName: p.name,
                        projectRef: {
                            workspaceId: p.workspaceId as WorkspaceId,
                            projectId: p.id,
                        },
                        origin: 'project' as PlannedTaskOrigin,
                    };
                });
        }),
        ...workspaceData.automations.map((a) => {
            return {
                task: {
                    id: a.id,
                    name: a.name,
                    planned: true,
                    state: 'PLANNED' as TaskState,
                    createdAt: new Date().toISOString(),
                    completedAt: a.completedAt,
                    done: false,
                },
                projectRef: {
                    workspaceId: workspaceData.id as WorkspaceId,
                    projectId: a.id as ProjectId,
                },
                origin: 'automation' as PlannedTaskOrigin,
            };
        }),
    ].sort(
        (t1, t2) =>
            workspaceData.plannedTasks.indexOf(t1.task.id) -
            workspaceData.plannedTasks.indexOf(t2.task.id)
    );

    const partitionedTasks = activeTasks.reduce(
        (acc: [PlannedTask[], PlannedTask[]], plannedTask) => {
            // Only include non-automation tasks in completed tasks
            if (plannedTask.origin === 'automation') {
                if (plannedTask.task.completedAt === null) {
                    acc[0].push(plannedTask);
                }
                // Ignore completed automations
            } else {
                acc[plannedTask.task.completedAt === null ? 0 : 1].push(
                    plannedTask
                );
            }
            return acc;
        },
        [[], []]
    );
    const _plannedTasks: PlannedTask[] = partitionedTasks[0];
    const completedTasks: PlannedTask[] = partitionedTasks[1];
    const displaySeparator =
        _plannedTasks.length > 0 && completedTasks.length > 0;

    const hideCompletedTasks = () => {
        dispatch(hideCompletedTasksAction());
    };

    const idleAllPlannedTasks = () => {
        dispatch(idleAllPlannedTasksAction());
    };

    const onQuickTasksAdded = async (tasks: string[]) => {
        for (let i = 0; i < tasks.length; i++) {
            const t = tasks[i];
            await dispatch(
                addTask({
                    workspaceId: workspaceId,
                    projectId: workspaceId,
                    name: t,
                    planned: true,
                })
            );
        }
    };

    return (
        <>
            <Container narrow>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent:
                            _plannedTasks.length > 0
                                ? 'space-between'
                                : 'center',
                        marginTop: _plannedTasks.length > 0 ? '0' : '40px',
                        marginBottom: _plannedTasks.length > 0 ? '0' : '40px',
                    }}
                >
                    <AddTask
                        buttonText={'+ Quick Add'}
                        placeholder={'Add task...'}
                        onTasksAdded={onQuickTasksAdded}
                    />
                    {_plannedTasks.length > 0 && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Dropdown position="bottomRight" horizontal={true}>
                                <Button onClick={idleAllPlannedTasks}>
                                    <MoveBackIcon
                                        style={{ display: 'inline' }}
                                    />
                                    <span style={{ marginLeft: '6px' }}>
                                        Move&nbsp;all&nbsp;tasks&nbsp;to&nbsp;planning
                                    </span>
                                </Button>
                            </Dropdown>
                        </div>
                    )}
                </div>
            </Container>
            {_plannedTasks.length === 0 && completedTasks.length === 0 && (
                <Container narrow>
                    <div 
                        className={'empty-state'}
                        style={{
                            textAlign: 'center',
                            padding: '60px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            background: 'var(--box-background)',
                            borderRadius: 'var(--radius)',
                            border: '1px solid var(--grey)',
                            marginBottom: '24px'
                        }}
                    >
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #FF6B6B, #4ECDC4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '24px',
                            fontSize: '32px'
                        }}>
                            🎯
                        </div>
                        <h3 style={{
                            margin: '0 0 12px 0',
                            fontSize: '20px',
                            fontWeight: '600',
                            color: 'var(--text)'
                        }}>
                            Ready to execute!
                        </h3>
                        <p style={{
                            margin: '0 0 24px 0',
                            fontSize: '16px',
                            lineHeight: '1.5',
                            color: 'var(--text)',
                            opacity: 0.7,
                            maxWidth: '400px'
                        }}>
                            No planned tasks yet. Time to relax! Add tasks from your planning workspace or use Quick Add above.
                        </p>
                        <div style={{
                            fontSize: '14px',
                            color: 'var(--text)',
                            opacity: 0.6,
                            fontWeight: '500'
                        }}>
                            ☝️ Use Quick Add above to get started
                        </div>
                    </div>
                </Container>
            )}
            {_plannedTasks.length > 0 && <PlannedTasksList />}
            <Container narrow>
                {completedTasks.length > 0 && (
                    <>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '16px',
                                paddingTop: displaySeparator ? '24px' : '16px',
                                borderTop: displaySeparator ? '1px solid var(--grey)' : 'none',
                            }}
                        >
                            <h3 style={{ 
                                margin: 0, 
                                fontSize: '16px', 
                                fontWeight: '600',
                                color: 'var(--text)',
                                opacity: 0.8
                            }}>
                                Completed tasks ({completedTasks.length})
                            </h3>
                            <Button 
                                theme="plain" 
                                size="s" 
                                onClick={hideCompletedTasks}
                                style={{ 
                                    opacity: 0.7,
                                    fontSize: '13px'
                                }}
                            >
                                Hide completed
                            </Button>
                        </div>
                        <Grid columns={1} gap={8}>
                            {completedTasks
                                .sort((t1, t2) =>
                                    t2.task.state.localeCompare(t1.task.state)
                                )
                                .map((task) => (
                                    <TaskExecute
                                        key={task.task.id}
                                        id={task.task.id}
                                        state="completed"
                                    />
                                ))}
                        </Grid>
                    </>
                )}
            </Container>
        </>
    );
};

export default ExecutePage;
